interface FunctionInfo {
  index: number
  name: string | null
  isAsync: boolean
  headerStart: number
  headerEnd: number
  bodyStart: number
  bodyEnd: number
  asyncInsertPos: number
}

/**
 * Пропускает строковый литерал или комментарий начиная с позиции i.
 * Возвращает новую позицию после пропуска, или -1 если ничего не пропущено.
 */
export function skipStringOrComment(script: string, i: number, len: number): number {
  const ch = script[i]

  if (ch === '"' || ch === "'" || ch === '`') {
    const quote = ch
    i++
    while (i < len) {
      if (script[i] === '\\') {
        i += 2
        continue
      }
      if (script[i] === quote) {
        i++
        break
      }
      i++
    }
    return i
  }

  if (ch === '/' && script[i + 1] === '/') {
    i += 2
    while (i < len && script[i] !== '\n') i++
    return i
  }

  if (ch === '/' && script[i + 1] === '*') {
    i += 2
    while (i < len - 1) {
      if (script[i] === '*' && script[i + 1] === '/') {
        i += 2
        break
      }
      i++
    }
    return i
  }

  return -1
}

/**
 * Поиск парной круглой скобки для списка параметров функции.
 */
function matchParens(script: string, openPos: number): number {
  let depth = 1
  const len = script.length
  let i = openPos + 1

  while (i < len && depth > 0) {
    const skipped = skipStringOrComment(script, i, len)
    if (skipped !== -1) {
      i = skipped
      continue
    }

    if (script[i] === '(') depth++
    else if (script[i] === ')') depth--

    i++
  }

  return depth === 0 ? i - 1 : -1
}

/**
 * Поиск парной фигурной скобки для тела функции.
 */
function matchBracesInScript(script: string, openPos: number): number {
  let depth = 1
  const len = script.length
  let i = openPos + 1

  while (i < len && depth > 0) {
    const skipped = skipStringOrComment(script, i, len)
    if (skipped !== -1) {
      i = skipped
      continue
    }

    if (script[i] === '{') depth++
    else if (script[i] === '}') depth--

    i++
  }

  return depth === 0 ? i - 1 : -1
}

/**
 * Для позиции запятой/скобки перед аргументом возвращает позицию открывающей
 * круглой скобки вызова, к которому принадлежит аргумент.
 */
function findCallOpenParen(script: string, fromPos: number): number {
  let depth = 0
  for (let i = fromPos; i >= 0; i--) {
    const ch = script[i]
    if (ch === ')') depth++
    else if (ch === '(') {
      if (depth === 0) return i
      depth--
    }
  }
  return -1
}

/**
 * `$("each")` / `$('each')` immediately before a call `(`.
 * `)` is not an identifier char, so the generic walk cannot see `$`.
 */
function matchDollarCallStart(script: string, closeParen: number): number {
  let depth = 1
  for (let i = closeParen - 1; i >= 0; i--) {
    if (script[i] === ')') depth++
    else if (script[i] === '(') {
      depth--
      if (depth === 0) {
        let j = i - 1
        while (j >= 0 && /\s/.test(script[j])) j--
        if (j >= 0 && script[j] === '$') return j
        return -1
      }
    }
  }
  return -1
}

/**
 * Находит начало идентификатора вызываемой функции перед заданной скобкой.
 */
function findCalleeStart(script: string, openParenPos: number): number {
  let i = openParenPos - 1
  while (i >= 0 && /\s/.test(script[i])) i--
  if (i < 0) return -1

  if (script[i] === ')') {
    const dollarStart = matchDollarCallStart(script, i)
    if (dollarStart >= 0) return dollarStart
  }

  // foo, obj.foo
  const isIdentChar = (ch: string) => /[A-Za-z0-9_$.\]]/.test(ch)

  let end = i
  while (end >= 0 && isIdentChar(script[end])) end--

  const start = end + 1
  return start <= i ? start : -1
}

/**
 * If a function expression at `fnHeaderStart` is a call argument, await that call.
 */
function addAwaitForEnclosingCallIfArg(
  script: string,
  fnHeaderStart: number,
  dest: Set<number>,
): void {
  let k = fnHeaderStart - 1
  while (k >= 0 && /\s/.test(script[k])) k--
  if (k < 0) return
  if (script[k] !== ',' && script[k] !== '(') return

  const openParen = findCallOpenParen(script, k)
  if (openParen < 0) return
  const calleeStart = findCalleeStart(script, openParen)
  if (calleeStart < 0) return
  const beforeCall = script.slice(Math.max(0, calleeStart - 20), calleeStart)
  if (/\bawait\s*$/.test(beforeCall)) return
  dest.add(calleeStart)
}

/**
 * Находит позиции всех вхождений await в скрипте, пропуская строки и комментарии.
 */
function findAwaitPositionsInScript(script: string): number[] {
  const positions: number[] = []
  const len = script.length
  let i = 0

  while (i < len) {
    const skipped = skipStringOrComment(script, i, len)
    if (skipped !== -1) {
      i = skipped
      continue
    }

    // Собственно await
    if (script.startsWith('await', i)) {
      const before = script[i - 1]
      const after = script[i + 5]
      const isWordBoundaryBefore = i === 0 || !/[A-Za-z0-9_$]/.test(before)
      const isWordBoundaryAfter = after === undefined || !/[A-Za-z0-9_$]/.test(after)
      if (isWordBoundaryBefore && isWordBoundaryAfter) {
        positions.push(i)
        i += 5
        continue
      }
    }

    i++
  }

  return positions
}

/**
 * Описание найденной функции в скрипте.
 */
function findFunctionsInScript(script: string): FunctionInfo[] {
  const result: FunctionInfo[] = []
  const len = script.length
  let i = 0
  let fnIndex = 0

  while (i < len) {
    const skipped = skipStringOrComment(script, i, len)
    if (skipped !== -1) {
      i = skipped
      continue
    }

    // function foo(...) { ... } или function (...) { ... }
    const funcMatch = script.slice(i).match(/^(async\s+)?function\s*(\w*)\s*\(/)
    if (funcMatch) {
      const isAsync = !!funcMatch[1]
      const name = funcMatch[2] || null
      const headerStart = i

      // Находим начало параметров и соответствующую закрывающую скобку
      const parenOpen = script.indexOf('(', headerStart)
      if (parenOpen === -1) {
        i += 'function'.length
        continue
      }
      const parenClose = matchParens(script, parenOpen)
      if (parenClose === -1) {
        i = parenOpen + 1
        continue
      }

      // После параметров должны идти пробелы/переносы и {
      let j = parenClose + 1
      while (j < len && /\s/.test(script[j])) j++
      if (j >= len || script[j] !== '{') {
        i = parenClose + 1
        continue
      }

      const bodyStart = j
      const bodyEnd = matchBracesInScript(script, bodyStart)
      if (bodyEnd === -1) {
        i = bodyStart + 1
        continue
      }

      const headerEnd = parenClose

      result.push({
        index: fnIndex++,
        name,
        isAsync,
        headerStart,
        headerEnd,
        bodyStart,
        bodyEnd,
        asyncInsertPos: headerStart,
      })

      i = bodyStart + 1
      continue
    }

    // Простейшая поддержка стрелочных функций вида: (args) => { ... }
    const arrowMatch = script.slice(i).match(/^(async\s+)?\([^)]*\)\s*=>\s*\{/)
    if (arrowMatch) {
      const isAsync = !!arrowMatch[1]
      const headerStart = i
      const bracePos = script.indexOf('{', headerStart)
      if (bracePos === -1) {
        i++
        continue
      }
      const bodyStart = bracePos
      const bodyEnd = matchBracesInScript(script, bodyStart)
      if (bodyEnd === -1) {
        i = bodyStart + 1
        continue
      }

      result.push({
        index: fnIndex++,
        name: null,
        isAsync,
        headerStart,
        headerEnd: bodyStart,
        bodyStart,
        bodyEnd,
        asyncInsertPos: headerStart,
      })

      i = bodyStart + 1
      continue
    }

    i++
  }

  return result
}

/**
 * Заменяет вызовы $("func_name") на await $("func_name") для всех асинхронных
 * функций, зарегистрированных в asyncImports со значением true
 */
function injectAwaitForWebapi(script: string, asyncImports: Record<string, boolean>): string {
  let result = script
  for (const [key, value] of Object.entries(asyncImports)) {
    if (value !== true) continue
    // Заменяем $("key") и $('key'), перед которыми нет await
    const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const pattern = new RegExp(`(?<!await\\s)\\$\\((['"])${escapedKey}\\1\\)`, 'g')
    result = result.replace(pattern, (match) => `await ${match}`)
  }
  return result
}

/**
 * Анализирует скрипт на вложенность вызовов await и:
 *  - добавляет async к сигнатурам функций, внутри которых есть await;
 *  - добавляет await к вызовам таких функций;
 *  - если такая функция передаётся как аргумент в вызов другой функции,
 *    добавляет await и к этому внешнему вызову.
 *
 * Работает итеративно, пока находятся новые функции с await без async.
 * Реализовано лёгким парсером без сторонних зависимостей, с учётом строк и комментариев.
 */
function injectAsyncForWebapi(script: string): string {
  let result = script
  const maxIterations = 20

  let iteration = 0
  let changed = true

  while (changed && iteration < maxIterations) {
    iteration++
    changed = false

    // Находим все функции в текущем скрипте
    const functions = findFunctionsInScript(result)
    if (!functions.length) break

    // Находим все позиции await (с учётом строк и комментариев)
    const awaitPositions = findAwaitPositionsInScript(result)
    if (!awaitPositions.length) break

    // Определяем, какие функции содержат await и при этом ещё не async
    const needAsyncByIndex = new Set<number>()
    for (const pos of awaitPositions) {
      // выбираем самую внутреннюю функцию, содержащую эту позицию
      let owner: FunctionInfo | null = null
      for (const fn of functions) {
        if (fn.bodyStart <= pos && pos < fn.bodyEnd) {
          if (!owner || fn.bodyStart >= owner.bodyStart) {
            owner = fn
          }
        }
      }
      if (owner && !owner.isAsync) {
        needAsyncByIndex.add(owner.index)
      }
    }

    // 1) Добавляем async к сигнатурам таких функций (с конца, чтобы не ломать индексы)
    const toAsync = functions
      .filter((f) => needAsyncByIndex.has(f.index) && !f.isAsync)
      .sort((a, b) => b.asyncInsertPos - a.asyncInsertPos)

    if (toAsync.length) {
      for (const fn of toAsync) {
        result = result.slice(0, fn.asyncInsertPos) + 'async ' + result.slice(fn.asyncInsertPos)
      }
      changed = true
      // после изменения текста пересчитаем функции/await на следующей итерации
      continue
    }

    // 2) Добавляем await к вызовам функций, которые уже async (по имени)
    const asyncNamedFunctions = functions.filter(
      (f) => (f.isAsync || needAsyncByIndex.has(f.index)) && !!f.name,
    )

    const awaitInsertPositions = new Set<number>()

    for (const fn of asyncNamedFunctions) {
      const name = fn.name as string

      // 2.1. Обычные вызовы по имени: foo(...)
      const callRegex = new RegExp(`\\b${name}\\s*\\(`, 'g')
      let match: RegExpExecArray | null
      while ((match = callRegex.exec(result)) !== null) {
        const callPos = match.index

        // Не трогаем место объявления функции: function foo(...
        const beforeDecl = result.slice(Math.max(0, callPos - 15), callPos)
        if (/function\s*$/.test(beforeDecl)) continue

        // Проверяем, нет ли уже await непосредственно перед вызовом
        const before = result.slice(Math.max(0, callPos - 20), callPos)
        if (/\bawait\s*$/.test(before)) continue

        awaitInsertPositions.add(callPos)
      }

      // 2.2. Если эта функция передаётся как аргумент, добавляем await к внешнему вызову
      // Ищем вхождения имени как аргумента: (..., name) или (name, ...)
      const argRegex = new RegExp(`\\b${name}\\b`, 'g')
      while ((match = argRegex.exec(result)) !== null) {
        const namePos = match.index

        // Определяем, действительно ли это аргумент вызова (после запятой или открывающей скобки)
        let k = namePos - 1
        while (k >= 0 && /\s/.test(result[k])) k--
        if (k < 0) continue
        if (result[k] !== ',' && result[k] !== '(') continue

        // Ищем открывающую скобку вызова, к которому принадлежит этот аргумент
        const openParen = findCallOpenParen(result, k)
        if (openParen < 0) continue

        // Находим начало имени вызываемой функции перед этой скобкой
        const calleeStart = findCalleeStart(result, openParen)
        if (calleeStart < 0) continue

        // Проверяем, нет ли уже await перед вызовом
        const beforeCall = result.slice(Math.max(0, calleeStart - 20), calleeStart)
        if (/\bawait\s*$/.test(beforeCall)) continue

        awaitInsertPositions.add(calleeStart)
      }
    }

    // Anonymous async callback as argument: `$("each")(table, async function() { await … })`
    for (const fn of functions) {
      if (!fn.isAsync && !needAsyncByIndex.has(fn.index)) continue
      addAwaitForEnclosingCallIfArg(result, fn.headerStart, awaitInsertPositions)
    }

    if (awaitInsertPositions.size) {
      // добавляем await с конца, чтобы не портить индексы
      [...awaitInsertPositions]
        .sort((a, b) => b - a)
        .forEach((pos) => {
          result = result.slice(0, pos) + 'await ' + result.slice(pos)
        })
      changed = true
    }
  }

  return result
}

const EN_ASYNC_METHOD_CALLS = ['reloadElement', 'reloadPanel', 'reloadForm', 'putFormData'] as const

/** Async UI-диалоги Messages (клиентский messagesService / JSContext). showToast — sync. */
const MESSAGES_ASYNC_METHOD_CALLS = [
  'showMessage',
  'showMessageError',
  'showConfirmDialog',
  'checkConfirmation',
  'showCheckBoxChooser',
  'showFilesToSignChooser',
  'showFilesChooser',
  'showChooser',
  'showInput',
  'showInputWithTitle',
  'showInputText',
  'showButtonChooser',
  'showButtonChooserByColumns',
  'showChooserWithTable',
] as const

function injectAwaitForEnAsyncMethods(script: string): string {
  let result = script
  for (const method of EN_ASYNC_METHOD_CALLS) {
    const pattern = new RegExp(`(?<!await\\s)en\\.${method}\\s*\\(`, 'g')
    result = result.replace(pattern, (match) => `await ${match}`)
  }
  return result
}

/**
 * `en.putFullData(name).getField` → `(await en.putFullData(name)).getField`.
 * Bare `await en.putFullData(name).getField` would await the chained call.
 * Statement `en.putFullData(name)` → `await en.putFullData(name)` without parens:
 * wrapping consecutive statements as `(await …)\n(await …)` is parsed as a call (ASI).
 */
function injectAwaitForEnPutFullData(script: string): string {
  const marker = 'en.putFullData'
  const wraps: Array<{ from: number; end: number; call: string; chained: boolean }> = []
  let i = 0
  const len = script.length

  while (i < len) {
    const skipped = skipStringOrComment(script, i, len)
    if (skipped !== -1) {
      i = skipped
      continue
    }
    if (!script.startsWith(marker, i)) {
      i += 1
      continue
    }
    let j = i + marker.length
    while (j < len && /\s/.test(script[j])) j += 1
    if (script[j] !== '(') {
      i += 1
      continue
    }
    const close = matchParens(script, j)
    if (close === -1) {
      i += 1
      continue
    }
    let after = close + 1
    while (after < len && /\s/.test(script[after])) after += 1
    const chained = script[after] === '.'
    const prefix = script.slice(Math.max(0, i - 16), i)
    if (/\(\s*await\s*$/.test(prefix)) {
      i = close + 1
      continue
    }
    const awaitPrefix = prefix.match(/\bawait\s*$/)
    if (awaitPrefix && !chained) {
      i = close + 1
      continue
    }
    const from = awaitPrefix ? i - awaitPrefix[0].length : i
    wraps.push({ from, end: close + 1, call: script.slice(i, close + 1), chained })
    i = close + 1
  }

  let result = script
  for (let w = wraps.length - 1; w >= 0; w -= 1) {
    const item = wraps[w]!
    const replacement = item.chained ? `(await ${item.call})` : `await ${item.call}`
    result = `${result.slice(0, item.from)}${replacement}${result.slice(item.end)}`
  }
  return result
}

/**
 * Swing-скрипты пишут `var answer = Messages.showConfirmDialog(...)` синхронно;
 * на web Messages.* возвращают Promise — ставим await (и Packages.ru...Messages.*).
 */
function injectAwaitForMessagesAsyncMethods(script: string): string {
  let result = script
  for (const method of MESSAGES_ASYNC_METHOD_CALLS) {
    const pattern = new RegExp(
      `(?<!await\\s)(?:Packages\\.ru\\.esoft\\.fatclient\\.tools\\.)?Messages\\.${method}\\s*\\(`,
      'g',
    )
    result = result.replace(pattern, (match) => `await ${match}`)
  }
  return result
}

/**
 * Swing: `MainFrame.getInstance().changeUser(...)` sync;
 * web: changeUser returns Promise — inject await (and Packages…MainFrame path).
 */
function injectAwaitForMainFrameChangeUser(script: string): string {
  const pattern =
    /(?<!await\s)(?:Packages\.ru\.esoft\.swingfatclient\.frames\.)?MainFrame\.getInstance\s*\(\s*\)\s*\.changeUser\s*\(/g
  return script.replace(pattern, (match) => `await ${match}`)
}

/**
 * Swing `getElement(...).invokeEvent(...)` is synchronous.
 * Web `invokeEvent` returns a Promise (nested onInlineMenuShow / runCode).
 */
function injectAwaitForInvokeEvent(script: string): string {
  const callRe =
    /^(?:en\.getElement\s*\((?:[^()"']|"[^"]*"|'[^']*')*\)|[A-Za-z_$][\w$]*)\s*\.\s*invokeEvent\s*\(/
  let result = ''
  let i = 0
  const len = script.length
  while (i < len) {
    const skipped = skipStringOrComment(script, i, len)
    if (skipped !== -1) {
      result += script.slice(i, skipped)
      i = skipped
      continue
    }
    const match = script.slice(i).match(callRe)
    if (match && match.index === 0) {
      const prefix = script.slice(Math.max(0, i - 16), i)
      result += /\bawait\s*$/.test(prefix) ? match[0] : `await ${match[0]}`
      i += match[0].length
      continue
    }
    result += script[i]
    i += 1
  }
  return result
}

/**
 * Полный пайплайн трансформации скрипта:
 *  0. await перед async-методами en.* (putFullData: `(await …)` только при цепочке `.`) / Messages.* / MainFrame.changeUser / *.invokeEvent
 *  1. Вставка await перед $("asyncFunc") по карте asyncImports
 *  2. Итеративная простановка async/await по функциям
 */
export function analyzeAndInjectAsync(
  script: string,
  asyncImports: Record<string, boolean>,
): string {
  const withEnAwaits = injectAwaitForEnPutFullData(injectAwaitForEnAsyncMethods(script))
  const withMessagesAwaits = injectAwaitForMessagesAsyncMethods(withEnAwaits)
  const withMainFrameAwaits = injectAwaitForMainFrameChangeUser(withMessagesAwaits)
  const withInvokeAwaits = injectAwaitForInvokeEvent(withMainFrameAwaits)
  const withAwaits = injectAwaitForWebapi(withInvokeAwaits, asyncImports)
  return injectAsyncForWebapi(withAwaits)
}

